import { isArchivedCase, type JobOrder } from '@/domain';
import { useUnarchiveCases } from '@/application/usecases/useBulk';
import { useSession } from '@/application/usecases/useSession';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { HoldNote } from '@/ui/data/StatusDot';
import { drawerStyles } from '@/ui/layout/Drawer';
import { useConfirmAction } from '@/features/shell/useConfirmAction';

/**
 * An archived case says so at the top of its record: when, by whom and why, and
 * for a holder of `archiveCase`, the way back out. Section 4 of the request of
 * 24 September 2026. Shaped as the deletion's note beside it, since the two are
 * the same kind of fact about a case: not a state, a block of its own.
 *
 * Taking it out is the selection's own command with one case in it, so the rule
 * and the words are the ones the Archived tab reads. It is asked about first,
 * in the shared confirmation: the request of 5 October 2026 (third), section 2.
 */
export function ArchiveNote({ order, className }: { order: JobOrder; className?: string }) {
  const session = useSession();
  const mayTakeOut = session.data?.permissions.includes('archiveCase') === true;
  const unarchive = useUnarchiveCases();
  const ask = useConfirmAction();

  // Through isArchivedCase, which reads an absent block as not archived.
  if (!isArchivedCase(order)) return null;
  const { archive, jobOrderNo } = order;

  const note = [
    strings.archived.banner,
    archive.archivedAt === null ? '' : `${strings.archived.bannerOn} ${formatDay(archive.archivedAt)}`,
    archive.archivedByUserName === null ? '' : `${strings.archived.bannerBy} ${archive.archivedByUserName}`,
  ]
    .filter((part) => part !== '')
    .join(' ');

  return (
    <div className={className}>
      <HoldNote>{note}</HoldNote>
      {archive.reason !== null && <p className={drawerStyles.deletionReason}>{archive.reason}</p>}

      {mayTakeOut && (
        <div className={drawerStyles.deletionActions}>
          <Button
            disabled={unarchive.isPending}
            onClick={() =>
              ask(
                strings.confirm.unarchive(jobOrderNo),
                () =>
                  unarchive.mutateAsync({ jobOrderNos: [jobOrderNo] }).then((result) => {
                    const outcome = result.results[0];
                    toast(
                      outcome?.ok === true
                        ? `${jobOrderNo} ${strings.bulk.unarchived}`
                        : (outcome?.detail ?? strings.errors.server),
                    );
                  }),
                { confirmLabel: strings.bulk.unarchive },
              )
            }
          >
            {unarchive.isPending ? strings.bulk.sending : strings.bulk.unarchive}
          </Button>
        </div>
      )}
    </div>
  );
}
